import * as turf from "@turf/turf";
import type mapboxgl from "mapbox-gl";
import type {
  TurfFeatureCollectionType,
  TurfLineStringType,
} from "../components/MapComponent/type";

interface Options {
  spacing?: number;
  color?: string;
  drawGreyLines?: boolean; // Control whether to draw grey intermediate lines
  visible?: boolean; // Control the layout visibility of the grey intermediate layer
}

/**
 * A field boundary used for clipping the machinery grid. May be a simple Polygon
 * or a MultiPolygon (several detached parcels).
 *
 * The grid-clipping math needs no special-casing for MultiPolygon: turf's
 * lineIntersect returns crossing points against every sub-polygon's boundary,
 * sortIntersectionsAlongLine orders them along the offset line, and
 * appendStraightSegments pairs consecutive crossings (enter→exit). For disjoint
 * parcels the crossings never interleave, so each pair is an in-field segment and
 * the gaps between parcels are skipped — drawing the grid inside every polygon.
 */
type FieldClipFeature = turf.Feature<turf.Polygon | turf.MultiPolygon>;

const DEFAULT_DRAW_MACHINERY_GRID_OPTIONS: Required<Options> = {
  spacing: 100,
  color: "#888",
  drawGreyLines: false,
  visible: true,
};

const isValidStepSize = (step: number): boolean => {
  return Number.isFinite(step) && step > 0;
};

const sortIntersectionsAlongLine = (
  intersections: turf.FeatureCollection<turf.Point>,
  line: turf.Feature<turf.LineString>
): Array<turf.Feature<turf.Point>> => {
  const sortedPoints = [...intersections.features];
  const lineStartPoint = turf.point(line.geometry.coordinates[0]);

  sortedPoints.sort((a, b) => {
    return (
      turf.distance(lineStartPoint, a, {
        units: "kilometers",
      }) -
      turf.distance(lineStartPoint, b, {
        units: "kilometers",
      })
    );
  });

  return sortedPoints;
};

/**
 * Connect consecutive intersection pairs with straight segments.
 *
 * The plot grid is built from rhumb lines, which render as *straight* lines in
 * the Web-Mercator projection mapbox draws in. Rendering machinery lines as
 * great-circle arcs (turf.greatCircle) instead makes them curve in Mercator,
 * so they drift away from the straight plot edges the farther they run from the
 * AB origin. Using a plain two-point segment keeps the machinery line straight
 * in Mercator, matching the plot grid exactly.
 */
const appendStraightSegments = (
  sortedPoints: Array<turf.Feature<turf.Point>>,
  targetLines: TurfLineStringType[]
): void => {
  for (let i = 0; i < sortedPoints.length - 1; i += 2) {
    targetLines.push(
      turf.lineString([
        sortedPoints[i].geometry.coordinates,
        sortedPoints[i + 1].geometry.coordinates,
      ])
    );
  }
};

const GRID_LINE_SAMPLE_KM = 2;
const GRID_LINE_FALLBACK_EXTENT_KM = 60;

/**
 * How far (km, each way from the AB origin) a grid line must run to be sure it
 * crosses the whole field. Uses the largest distance from the AB origin to any
 * field bounding-box corner, plus a margin, so the line spans any field size
 * without over-sampling small ones.
 */
export const computeLineExtentKm = (
  start: number[],
  polygon: FieldClipFeature
): number => {
  try {
    const [minX, minY, maxX, maxY] = turf.bbox(polygon);
    const corners: Array<[number, number]> = [
      [minX, minY],
      [maxX, minY],
      [maxX, maxY],
      [minX, maxY],
    ];
    const origin = turf.point(start);
    const maxDist = Math.max(
      ...corners.map((corner) =>
        turf.distance(origin, turf.point(corner), { units: "kilometers" })
      )
    );
    return Number.isFinite(maxDist) && maxDist > 0
      ? maxDist + 1
      : GRID_LINE_FALLBACK_EXTENT_KM;
  } catch {
    return GRID_LINE_FALLBACK_EXTENT_KM;
  }
};

/**
 * The field's signed extent PERPENDICULAR to the AB line (km), measured from the
 * four bounding-box corners. Every machinery grid line lives at a multiple of the
 * spacing within [minKm, maxKm], so this tells us up front exactly which offsets
 * can cross the field — no blind fixed-iteration scan. The sign convention matches
 * buildParallelRhumbLine (positive offset = bearing + 90°): a corner clockwise of
 * the AB direction yields a positive perpendicular offset.
 */
const computePerpRangeKm = (
  start: number[],
  bearing: number,
  polygon: FieldClipFeature
): { minKm: number; maxKm: number } => {
  try {
    const [minX, minY, maxX, maxY] = turf.bbox(polygon);
    const corners: Array<[number, number]> = [
      [minX, minY],
      [maxX, minY],
      [maxX, maxY],
      [minX, maxY],
    ];
    const origin = turf.point(start);
    let minKm = Infinity;
    let maxKm = -Infinity;
    for (const corner of corners) {
      const point = turf.point(corner);
      const distKm = turf.distance(origin, point, { units: "kilometers" });
      const theta = ((turf.bearing(origin, point) - bearing) * Math.PI) / 180;
      const perpKm = distKm * Math.sin(theta);
      if (perpKm < minKm) minKm = perpKm;
      if (perpKm > maxKm) maxKm = perpKm;
    }
    if (Number.isFinite(minKm) && Number.isFinite(maxKm)) {
      return { minKm, maxKm };
    }
  } catch {
    /* fall through to a wide-but-bounded fallback */
  }
  return {
    minKm: -GRID_LINE_FALLBACK_EXTENT_KM,
    maxKm: GRID_LINE_FALLBACK_EXTENT_KM,
  };
};

/**
 * The exact list of offsets (km) to test for a field: every multiple of `stepKm`
 * (shifted by `phaseKm` — 0 for grid lines, stepKm/2 for the grey intermediate
 * lines) within the bounding box's perpendicular extent, padded by half a grid
 * spacing plus a small fixed margin so an offset line sitting right on the field
 * edge is never missed. Deterministic and small (≈ field width / spacing), so the
 * line count is known before drawing — no blind 500 km / 5000-step scan, and the
 * count is bounded by the bounding box itself rather than an arbitrary cap.
 */
const OFFSET_EDGE_MARGIN_KM = 0.04; // ~40 m beyond the bbox, on top of half a spacing

const offsetsAcrossField = (
  start: number[],
  bearing: number,
  polygon: FieldClipFeature,
  stepKm: number,
  phaseKm = 0
): number[] => {
  if (!isValidStepSize(stepKm)) return [];
  const { minKm, maxKm } = computePerpRangeKm(start, bearing, polygon);
  const marginKm = stepKm / 2 + OFFSET_EDGE_MARGIN_KM;
  const kMin = Math.floor((minKm - marginKm - phaseKm) / stepKm);
  const kMax = Math.ceil((maxKm + marginKm - phaseKm) / stepKm);
  if (!Number.isFinite(kMin) || !Number.isFinite(kMax) || kMax < kMin) {
    return [];
  }
  const offsets: number[] = [];
  for (let k = kMin; k <= kMax; k += 1) {
    offsets.push(k * stepKm + phaseKm);
  }
  return offsets;
};

/**
 * Build a line parallel to the AB line, offset `offsetKm` kilometres
 * perpendicular to it, using rhumb geometry throughout so the offset is a
 * uniform perpendicular distance everywhere along the line (matching how the
 * plot grid and `snapGridToAbLine` measure offsets).
 *
 * The line is returned as a densely-sampled rhumb polyline (extending
 * `extentKm` each way) so that both turf.lineIntersect (great-circle per
 * segment) and mapbox rendering follow the true constant-bearing path.
 */
export const buildParallelRhumbLine = (
  start: number[],
  bearing: number,
  offsetKm: number,
  extentKm: number
): TurfLineStringType => {
  const perpDistance = Math.abs(offsetKm);
  const perpBearing = offsetKm >= 0 ? bearing + 90 : bearing - 90;

  // Offset the AB origin perpendicular by the requested rhumb distance, then
  // walk the constant-bearing line outward from there in both directions.
  const origin =
    perpDistance === 0
      ? turf.point(start)
      : turf.rhumbDestination(turf.point(start), perpDistance, perpBearing, {
          units: "kilometers",
        });

  // Walk the constant-bearing line from -extentKm to +extentKm, ALWAYS emitting
  // both endpoints. A plain `d += GRID_LINE_SAMPLE_KM` loop drops the final
  // +extentKm sample whenever extentKm isn't a multiple of the step — and for any
  // field smaller than GRID_LINE_SAMPLE_KM (2 km) it stops one step past the
  // negative end, truncating the line so it terminates INSIDE the field. That gave
  // single-crossing offset lines (impossible for a closed ring), which the >=2
  // guard skipped, leaving a hollow band through the middle of the field. Using a
  // fixed sample count keeps the endpoints exact and the spacing <= the step.
  const totalKm = 2 * extentKm;
  const sampleCount = Math.max(2, Math.ceil(totalKm / GRID_LINE_SAMPLE_KM));
  const stepKm = totalKm / sampleCount;
  const coords: number[][] = [];
  for (let i = 0; i <= sampleCount; i += 1) {
    const d = -extentKm + i * stepKm;
    const point =
      d >= 0
        ? turf.rhumbDestination(origin, d, bearing, { units: "kilometers" })
        : turf.rhumbDestination(origin, -d, bearing + 180, {
            units: "kilometers",
          });
    coords.push(turf.getCoord(point));
  }

  return turf.lineString(coords);
};

export function drawMachineryGridPure(
  map: mapboxgl.Map,
  turfPolygon: FieldClipFeature,
  start: number[],
  end: number[],
  gridSourceId: string,
  lineId: string,
  options?: Options
): void {
  const { spacing, color, drawGreyLines, visible } = {
    ...DEFAULT_DRAW_MACHINERY_GRID_OPTIONS,
    ...options,
  };
  const visibility = visible ? "visible" : "none";

  // Bearing must come from the original (short) AB-line points.
  const bearing = turf.bearing(turf.point(start), turf.point(end));

  // Generate regular machinery grid lines at original spacing (UNCHANGED)
  const gridLines = createGridLines(start, turfPolygon, spacing, bearing);

  // Generate additional gray intermediate lines at x/2 offsets (only if drawGreyLines is true)
  const grayIntermediateLines = drawGreyLines
    ? createIntermediateGridLines(start, turfPolygon, spacing, bearing)
    : [];

  // Clean up existing layers
  const graySourceId = `${gridSourceId}-gray`;
  const grayLayerId = `${lineId}-gray`;

  if (map.getLayer(grayLayerId)) map.removeLayer(grayLayerId);
  if (map.getSource(graySourceId)) map.removeSource(graySourceId);

  // Add gray intermediate lines first (behind regular lines) - only if drawGreyLines is true
  if (drawGreyLines && grayIntermediateLines.length > 0) {
    const grayGridData: TurfFeatureCollectionType = {
      type: "FeatureCollection",
      features: grayIntermediateLines,
    };

    map.addSource(graySourceId, { type: "geojson", data: grayGridData });
    map.addLayer({
      id: grayLayerId,
      type: "line",
      source: graySourceId,
      layout: { visibility },
      paint: {
        "line-color": "rgba(101, 90, 90, 0.4)", // Custom color for gray intermediate lines
        "line-width": 2,
      },
    });
  }

  // Add regular machinery grid lines (ORIGINAL FUNCTIONALITY PRESERVED)
  const gridData: TurfFeatureCollectionType = {
    type: "FeatureCollection",
    features: gridLines,
  };

  if (map.getSource(gridSourceId)) {
    (map.getSource(gridSourceId) as mapboxgl.GeoJSONSource).setData(gridData);
  } else {
    map.addSource(gridSourceId, { type: "geojson", data: gridData });
    map.addLayer({
      id: lineId,
      type: "line",
      source: gridSourceId,
      paint: { "line-color": color, "line-width": 2 },
    });
  }
}

/**
 * Clip a parallel rhumb line (offset `offsetKm` from the AB line) to the field
 * polygon and push the in-field segments onto `gridLines`.
 */
const appendOffsetLine = (
  start: number[],
  bearing: number,
  offsetKm: number,
  polygon: FieldClipFeature,
  gridLines: TurfLineStringType[],
  extentKm: number
): void => {
  const offsetLine = buildParallelRhumbLine(start, bearing, offsetKm, extentKm);
  const intersections = turf.lineIntersect(offsetLine, polygon);
  if (intersections.features.length >= 2) {
    const sortedPoints = sortIntersectionsAlongLine(intersections, offsetLine);
    appendStraightSegments(sortedPoints, gridLines);
  }
};

function createGridLines(
  start: number[],
  polygon: FieldClipFeature,
  spacing: number,
  baseLineBearing: number
): TurfLineStringType[] {
  const gridLines: TurfLineStringType[] = [];
  const step = spacing / 1000; // convert spacing to kilometers
  const extentKm = computeLineExtentKm(start, polygon);

  // The baseline itself (offset 0) is always attempted; it is drawn even when the
  // spacing is invalid (which leaves the rest of the grid empty).
  appendOffsetLine(start, baseLineBearing, 0, polygon, gridLines, extentKm);

  if (!isValidStepSize(step)) {
    return gridLines;
  }

  // Only the offsets the bounding box says can cross the field — no blind scan.
  for (const offsetKm of offsetsAcrossField(
    start,
    baseLineBearing,
    polygon,
    step
  )) {
    if (offsetKm === 0) continue; // baseline already drawn above
    appendOffsetLine(
      start,
      baseLineBearing,
      offsetKm,
      polygon,
      gridLines,
      extentKm
    );
  }

  return gridLines;
}

function createIntermediateGridLines(
  start: number[],
  polygon: FieldClipFeature,
  spacing: number,
  baseLineBearing: number
): TurfLineStringType[] {
  const gridLines: TurfLineStringType[] = [];
  const halfSpacing = spacing / 2;
  const halfStep = halfSpacing / 1000; // convert half spacing to kilometers
  const fullStep = spacing / 1000; // convert full spacing to kilometers
  const extentKm = computeLineExtentKm(start, polygon);

  if (!isValidStepSize(fullStep) || !isValidStepSize(halfStep)) {
    return gridLines;
  }

  // Intermediate lines sit halfway between the grid lines (phase = half a step):
  // ±halfSpacing, ±(spacing + halfSpacing)… — only those that cross the field.
  for (const offsetKm of offsetsAcrossField(
    start,
    baseLineBearing,
    polygon,
    fullStep,
    halfStep
  )) {
    appendOffsetLine(
      start,
      baseLineBearing,
      offsetKm,
      polygon,
      gridLines,
      extentKm
    );
  }

  return gridLines;
}

// ===========================================================================================
// 🎯 SMART STRIP OFFSET CALCULATION UTILITIES
// ===========================================================================================

/**
 * Information about grid lines relative to AB-line position
 */
export interface GridLinesInfo {
  totalLines: number; // Total number of grid lines
  leftLines: number; // Lines to the left of AB-line
  rightLines: number; // Lines to the right of AB-line
  abLinePosition: number; // AB-line position in the grid (0-based index)
  spacing: number; // Spacing between grid lines (meters)
  lineOffsets: number[]; // Distance of each line from AB-line (negative=left, positive=right)
}

/**
 * Analyze grid lines to get position information for smart strip calculation
 * This function reuses the existing grid generation logic but extracts position data
 */
export function getGridLinesInfo(
  start: number[],
  end: number[],
  polygon: FieldClipFeature,
  spacing: number
): GridLinesInfo {
  // Bearing must come from the original (short) AB-line points — see comment in
  // drawMachineryGridPure. Offsets use the same rhumb geometry as the rendered
  // grid so strip positions line up exactly with the drawn machinery lines.
  const baseLineBearing = turf.bearing(turf.point(start), turf.point(end));
  const step = spacing / 1000; // convert spacing to kilometers
  const extentKm = computeLineExtentKm(start, polygon);

  let leftLines = 0;
  let rightLines = 0;
  const lineOffsets: number[] = [];

  // First check if baseline itself intersects
  const baselineIntersections = turf.lineIntersect(
    buildParallelRhumbLine(start, baseLineBearing, 0, extentKm),
    polygon
  );
  if (baselineIntersections.features.length >= 2) {
    lineOffsets.push(0);
  }

  // Count the grid lines the bounding box says can cross the field (offset 0 is
  // the baseline, handled above), classifying each by side (negative = left).
  for (const offsetKm of offsetsAcrossField(
    start,
    baseLineBearing,
    polygon,
    step
  )) {
    if (offsetKm === 0) continue;
    const offsetLine = buildParallelRhumbLine(
      start,
      baseLineBearing,
      offsetKm,
      extentKm
    );
    const intersections = turf.lineIntersect(offsetLine, polygon);
    if (intersections.features.length >= 2) {
      lineOffsets.push(offsetKm * 1000); // back to meters
      if (offsetKm < 0) {
        leftLines++;
      } else {
        rightLines++;
      }
    }
  }

  // Sort offsets from left to right
  lineOffsets.sort((a, b) => a - b);

  // Find AB-line position in sorted array
  const abLinePosition = lineOffsets.indexOf(0);
  const totalLines = lineOffsets.length;

  const gridInfo: GridLinesInfo = {
    totalLines,
    leftLines,
    rightLines,
    abLinePosition,
    spacing,
    lineOffsets,
  };

  return gridInfo;
}

const roundOffsetTo3 = (value: number): number =>
  Math.round(value * 1000) / 1000;

/**
 * The only valid positions for a strip line: the machinery grid lines
 * (multiples of the AB spacing that cross the field) PLUS the grey intermediate
 * lines halfway between consecutive grid lines. Strip lines may never be placed
 * off these positions.
 *
 * Derived from the grid lines the field actually intersects, so the returned
 * positions span exactly the field's perpendicular extent.
 */
export function getStripSnapPositions(gridInfo: GridLinesInfo): number[] {
  const gridOffsets = [
    ...new Set(gridInfo.lineOffsets.map(roundOffsetTo3)),
  ].sort((a, b) => a - b);
  const positions = new Set<number>(gridOffsets);
  for (let i = 0; i < gridOffsets.length - 1; i++) {
    // Grey line at the midpoint between two adjacent grid lines.
    positions.add(roundOffsetTo3((gridOffsets[i] + gridOffsets[i + 1]) / 2));
  }
  return [...positions].sort((a, b) => a - b);
}

/**
 * Pick `lineCount` positions out of `positions`, spread as evenly as possible so
 * the resulting strips are ~equal width (quantised to the grid/grey lines).
 */
function pickEvenlySpacedPositions(
  positions: number[],
  lineCount: number
): number[] {
  const total = positions.length;
  if (lineCount <= 0 || total === 0) return [];
  // Not enough distinct positions — use every one of them (caller/validation
  // caps the strip count so this only happens at the field's true maximum).
  if (lineCount >= total) return [...positions];

  const used = new Set<number>();
  const chosen: number[] = [];
  for (let j = 1; j <= lineCount; j++) {
    let idx = Math.round((j * total) / (lineCount + 1) - 0.5);
    idx = Math.max(0, Math.min(total - 1, idx));
    while (used.has(idx) && idx < total - 1) idx += 1;
    while (used.has(idx) && idx > 0) idx -= 1;
    if (used.has(idx)) continue;
    used.add(idx);
    chosen.push(positions[idx]);
  }
  return chosen.sort((a, b) => a - b);
}

/**
 * Calculate strip offsets for `stripsCount` strips. The N-1 dividing lines are
 * chosen from the machinery grid + grey line positions only (never off-grid),
 * spread evenly for ~equal-width strips. N strips need N-1 lines, so the field
 * can hold at most getStripSnapPositions(...).length + 1 strips.
 */
export function calculateSmartStripOffsets(
  stripsCount: number,
  gridInfo: GridLinesInfo
): number[] {
  if (stripsCount < 2) {
    return [0];
  }

  const positions = getStripSnapPositions(gridInfo);

  if (positions.length === 0) {
    // No usable grid lines (e.g. field/AB geometry unavailable). Fall back to
    // AB-line-relative grid spacing — still multiples of the machinery spacing.
    const spacing = gridInfo.spacing || 50;
    const offsets: number[] = [];
    for (let i = 1; i < stripsCount; i++) {
      offsets.push(i * spacing);
    }
    return offsets;
  }

  return pickEvenlySpacedPositions(positions, stripsCount - 1);
}
